import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck } from "lucide-react";
import { IMAGES, HERO_IMAGE_MASK } from "@/lib/data";
import { galleryImageUrl, useGalleryImages } from "@/lib/gallery";

const EASE = [0.22, 1, 0.36, 1];

const HERO_MASK_CLASSES = {
    buet: "overflow-hidden rounded-t-[999px] rounded-b-3xl border-4 border-paper shadow-[0_30px_80px_rgba(6,38,54,0.5)]",
    firkantet: "overflow-hidden border-4 border-paper shadow-[0_30px_80px_rgba(6,38,54,0.5)]",
    uden: "",
};

function MaskedLine({ children, delay = 0 }) {
    return (
        <span className="block overflow-hidden pb-1">
            <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

function HeroCarousel({ images }) {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (images.length <= 1) return;
        const t = setInterval(() => setIndex((i) => (i + 1) % images.length), 4500);
        return () => clearInterval(t);
    }, [images.length]);

    if (!images.length) {
        return (
            <img
                src={IMAGES.hero}
                alt="Rød dansk pølse i brød med ristede løg og sennep"
                data-testid="hero-image"
                className="aspect-[4/5] w-full object-cover"
            />
        );
    }

    const current = images[index];
    return (
        <div className="relative aspect-[4/5] w-full">
            <AnimatePresence initial={false}>
                <motion.img
                    key={current.id}
                    src={galleryImageUrl(current)}
                    alt={current.description || "Pølsevogn"}
                    data-testid="hero-image"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.1, ease: "easeInOut" }}
                    className="absolute inset-0 h-full w-full object-cover"
                />
            </AnimatePresence>
        </div>
    );
}

export default function Hero({ onCta, onMenu }) {
    const ref = useRef(null);
    const { images } = useGalleryImages();
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
    const circleY = useTransform(scrollYProgress, [0, 1], [0, -70]);

    return (
        <section ref={ref} data-testid="hero-section" className="grain relative overflow-hidden bg-poelse pb-24 pt-32 sm:pt-40">
            <div className="mx-auto grid max-w-6xl items-center gap-14 px-5 lg:grid-cols-12">
                <div className="lg:col-span-7">
                    <h1 className="font-serif text-4xl font-bold leading-[1.05] tracking-tight text-paper sm:text-5xl lg:text-6xl">
                        <MaskedLine delay={0.2}>Til hverdag og fest –</MaskedLine>
                        <MaskedLine delay={0.34}>Lej en pølsevogn</MaskedLine>
                        <MaskedLine delay={0.48}>
                            er <em className="text-gold">bedst.</em>
                        </MaskedLine>
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
                        className="mt-7 max-w-md text-base leading-relaxed text-paper/70 sm:text-lg"
                    >
                        Mere end 25 års erfaring. Vi kører over hele Sjælland, Fyn, Jylland og
                        Øerne – Intet sted er for stort eller småt.
                        <br />
                        For at vi med vognen stopper op.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-cta-configurator"
                            onClick={onCta}
                            className="group inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-base font-bold text-poelse transition-[background-color,transform] duration-200 hover:bg-gold-deep active:scale-95"
                        >
                            Byg din event
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <button
                            data-testid="hero-cta-menu"
                            onClick={onMenu}
                            className="rounded-full border border-paper/30 px-7 py-3.5 text-base font-semibold text-paper transition-[border-color,background-color] duration-200 hover:border-paper/60 hover:bg-paper/5"
                        >
                            Se menuerne
                        </button>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.05 }}
                        className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1.5 text-xs font-semibold text-gold"
                        data-testid="hero-trust-pill"
                    >
                        <BadgeCheck className="h-4 w-4" />
                        Vælg en menu og datoen – Send en 100% uforpligtende forespørgsel
                    </motion.div>
                </div>
                <div className="relative lg:col-span-5">
                    <motion.div
                        style={{ y: circleY }}
                        className="absolute -left-8 top-6 h-72 w-72 rounded-full bg-gold/15 sm:h-96 sm:w-96"
                    />
                    <motion.div
                        style={{ y: imgY }}
                        initial={{ opacity: 0, y: 60 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.1, delay: 0.4, ease: EASE }}
                        className="relative"
                    >
                        <div className={HERO_MASK_CLASSES[HERO_IMAGE_MASK] || HERO_MASK_CLASSES.buet}>
                            <HeroCarousel images={images} />
                        </div>
                        <div className="absolute -right-4 -top-4 sm:-right-6">
                            <div className="spin-slow relative h-28 w-28">
                                <svg viewBox="0 0 100 100" className="h-full w-full">
                                    <defs>
                                        <path id="stamp-circle" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" />
                                    </defs>
                                    <circle cx="50" cy="50" r="49" fill="#F7D794" />
                                    <text fill="#0C5678" fontSize="10.5" fontFamily="Courier Prime, monospace" letterSpacing="2.5">
                                        <textPath href="#stamp-circle">UFORPLIGTENDE • FORESPØRGSEL • UFORPLIGTENDE •</textPath>
                                    </text>
                                </svg>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
